import { Component, Input, inject } from '@angular/core';
import {
    Attachments,
    BasketAddress,
    BasketItem,
    IPurchasingGroupResolution,
    SupplierBasket,
    TotalBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { IFileResponse } from '@lib/core';
import { I18nService } from '@lib/i18n';

/**
 * Dedicated read-only order summary for the last basket step. Renders a compact confirmation view
 * of all entered information; empty values are omitted instead of showing blank fields. Intentionally
 * separate from the editable supplier tree so the summary stays lightweight and free of editing scaffolding.
 */
@Component({
    selector: 'basket-summary',
    templateUrl: './basket-summary.component.html',
    styleUrls: ['./basket-summary.component.scss'],
    standalone: false,
})
export class BasketSummaryComponent {
    @Input() supplierBaskets: SupplierBasket[] = [];
    @Input() deliveryAddress: BasketAddress | undefined;
    @Input() invoiceAddress: BasketAddress | undefined;
    // Net/VAT/gross totals per currency; same data and block as the editable basket step.
    @Input() currencyTotals: TotalBasket[] = [];
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    // Defaults to false so the purchasing group stays hidden unless the tenant setting
    // (shownInOrderProcess) is forwarded as true, keeping the summary consistent with Step 1.
    @Input() purchasingGroupVisible = false;

    private readonly i18n = inject(I18nService);

    /**
     * Unlike the other accounting fields (which arrive with a flat, pre-resolved name), the basket view
     * delivers the material group as a live-resolved DTO carrying its localized names. Resolve the name
     * for the current UI language — falling back to English, then the first entry — so the summary shows
     * "code – name" like every other field instead of a bare code.
     */
    materialGroupName(item: BasketItem): string {
        const names = item.materialGroup?.names ?? [];
        if (names.length === 0) {
            return '';
        }

        const preferred = this.i18n.currentLanguage?.toLowerCase();
        const byLanguage = (language?: string): string =>
            names.find((entry) => entry.language?.toLowerCase() === language)?.value ?? '';

        return byLanguage(preferred) || byLanguage('en') || names[0]?.value || '';
    }

    /**
     * Render items in a stable order (by id ascending, i.e. the order they were added) so the
     * summary matches the editable basket steps instead of the aggregate endpoint's arbitrary order.
     */
    sortedItems(supplierBasket: SupplierBasket): BasketItem[] {
        return [...(supplierBasket.basketItems ?? [])].sort((a, b) => a.id - b.id);
    }

    thumbnailSrc(item: BasketItem): string | undefined {
        const images = item.product?.images ?? [];
        const thumbnail = images.find((image) => image.purpose === 'thumbnail') ?? images[0];
        return thumbnail?.src;
    }

    /**
     * Only surface the content unit when it actually differs from the order unit. When they are the same
     * code (the common case) it merely repeats the unit already shown next to the quantity.
     */
    showContentUnit(item: BasketItem): boolean {
        const product = item.product;
        return !!product?.contentUnit && product.contentUnit !== product.orderUnit;
    }

    /** True when the item has at least one filled detail field worth rendering in the summary. */
    hasItemDetails(item: BasketItem): boolean {
        const product = item.product;
        const manufacturer = product?.manufacturer;
        const candidates: unknown[] = [
            product?.supplierProductNumber,
            product?.customerProductNumber,
            manufacturer?.name,
            manufacturer?.productNumber,
            this.showContentUnit(item),
            item.desiredDeliveryDate,
            item.costCenter?.key,
            item.generalLedgerAccount?.key,
            item.project?.key,
            item.customAccountAssignments?.length,
            item.classificationCode?.code,
            item.materialGroup?.code,
            item.siteCode,
            item.internalNote,
        ];
        return candidates.some(Boolean);
    }

    /**
     * Resolved purchasing group label ("Code – Name") for an item, matched by supplier-basket and item id
     * the same way Step 1 does. Returns null when the tenant hides purchasing groups in the ordering
     * process (purchasingGroupVisible=false) or when no assignment resolved for the item, so the summary
     * simply omits the row instead of rendering an empty placeholder.
     */
    purchasingGroupLabel(supplierBasket: SupplierBasket, item: BasketItem): string | null {
        if (!this.purchasingGroupVisible) {
            return null;
        }
        const resolution = this.resolvedPurchasingGroups.find(
            (entry) =>
                entry.supplierBasketId === supplierBasket.id && entry.basketItemId === String(item.id),
        );
        const code = resolution?.purchasingGroupCode?.trim() || null;
        const name = resolution?.purchasingGroupName?.trim() || null;
        return code && name ? `${code} – ${name}` : code ?? name;
    }

    /**
     * Attachments reach this read-only summary as the {@link Attachments} shape ({@code name}/{@code link}),
     * even though the shared model types them as {@link IFileResponse}. Resolve both shapes defensively so the
     * file name and download link always render instead of an empty paperclip.
     */
    attachmentName(file: Attachments | IFileResponse): string {
        return (file as Attachments)?.name ?? (file as IFileResponse)?.fileName ?? '';
    }

    attachmentLink(file: Attachments | IFileResponse): string | undefined {
        return (file as Attachments)?.link ?? (file as IFileResponse)?.fileLink;
    }

    hasAddress(address: BasketAddress | undefined): boolean {
        if (!address) {
            return false;
        }
        const customer = address.contactInfo?.customerInfo;
        const company = address.contactInfo?.companyInfo;
        const location = address.address;
        return !!(
            customer?.firstName ||
            customer?.lastName ||
            company?.name ||
            company?.name2 ||
            location?.street ||
            location?.zip ||
            location?.city ||
            location?.country
        );
    }
}
